import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { BookOpen, FileDown, ClipboardCheck, Sparkles, ExternalLink, Info } from "lucide-react";

/**
 * ClauseIndexCardDialog — read-only "index card" view for a clause.
 *
 * Reused from Master Library, Compliance Coverage, V&V/Checklist detail pages,
 * and audit run report clause chips. Fetches full clause metadata + attachments
 * (title, description, PDF/image file) via GET /api/library/clauses/{cid}.
 */
export default function ClauseIndexCardDialog({ clauseId, onClose }) {
  const [clause, setClause] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!clauseId) return;
    setLoading(true);
    api.get(`/library/clauses/${clauseId}`)
      .then((r) => setClause(r.data))
      .catch(() => setClause(null))
      .finally(() => setLoading(false));
  }, [clauseId]);

  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const token = localStorage.getItem("opscmd_token") || "";
  const fileHref = (fid) => `${backend}/api/files/${fid}?auth=${encodeURIComponent(token)}`;

  return (
    <Dialog open={!!clauseId} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-3xl rounded-2xl" data-testid="clause-index-card">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-1">
            <BookOpen className="w-4 h-4 text-[#ff4500]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#ff4500] font-bold">
              // Clause · Index Card
            </span>
          </div>
          <DialogTitle className="font-display font-black text-xl">
            {loading ? "Loading…" : (clause?.title || "Not found")}
          </DialogTitle>
          {clause && (
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {(clause.citations || []).map((cit, i) => (
                <span key={i} className="font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#1a1a1a] text-white">
                  {cit.framework?.toUpperCase()} {cit.ref}
                </span>
              ))}
              <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-[#f7f5f2] text-[#1a1a1a]/70">
                {clause.category}
              </span>
            </div>
          )}
        </DialogHeader>

        {clause && (
          <div className="space-y-5">
            {clause.description && (
              <Panel icon={<Info className="w-4 h-4 text-[#1a5fb4]" />} title="What this clause requires">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{clause.description}</p>
              </Panel>
            )}

            {clause.tips && (
              <Panel icon={<Sparkles className="w-4 h-4 text-[#d6a900]" />} title="Tips · How to fulfill it">
                <p className="text-sm leading-relaxed whitespace-pre-wrap">{clause.tips}</p>
              </Panel>
            )}

            {(clause.attachments || []).length > 0 && (
              <Panel icon={<FileDown className="w-4 h-4 text-[#ff4500]" />} title={`Reference documents (${clause.attachments.length})`}>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {clause.attachments.map((a, i) => (
                    <a key={i} href={fileHref(a.file_id)} target="_blank" rel="noreferrer"
                      data-testid={`clause-att-${i}`}
                      className="group border border-[#1a1a1a]/12 rounded-xl p-3 hover:border-[#ff4500] hover:shadow-md bg-white transition-all block">
                      <AttachmentPreview att={a} fileHref={fileHref} />
                      <div className="mt-2 font-display font-bold text-[13px] line-clamp-2 group-hover:text-[#ff4500]">
                        {a.title || a.file_meta?.original_name}
                      </div>
                      {a.description && (
                        <div className="text-[10px] text-[#1a1a1a]/60 mt-1 line-clamp-2">{a.description}</div>
                      )}
                      <div className="text-[9px] font-mono uppercase tracking-wider text-[#1a1a1a]/40 mt-1.5 flex items-center gap-1">
                        <ExternalLink className="w-2.5 h-2.5" />
                        {a.file_meta?.mime_type?.includes("pdf") ? "PDF" : a.file_meta?.mime_type?.split("/")[0]?.toUpperCase() || "FILE"}
                      </div>
                    </a>
                  ))}
                </div>
              </Panel>
            )}

            {((clause.vv_sub_tasks || []).length > 0 || (clause.checklist_items || []).length > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {clause.vv_sub_tasks?.length > 0 && (
                  <Panel icon={<ClipboardCheck className="w-4 h-4 text-[#a13ad6]" />} title={`V&V sub-tasks (${clause.vv_sub_tasks.length})`}>
                    <ul className="space-y-1 text-[12px]">
                      {clause.vv_sub_tasks.map((s, i) => (
                        <li key={i} className="flex gap-2">
                          <span className="font-mono text-[9px] uppercase tracking-wider font-bold text-[#a13ad6] shrink-0 mt-0.5">
                            {(s.section || "verify").slice(0, 5)}
                          </span>
                          <span>{s.title}</span>
                        </li>
                      ))}
                    </ul>
                  </Panel>
                )}
                {clause.checklist_items?.length > 0 && (
                  <Panel icon={<ClipboardCheck className="w-4 h-4 text-[#1a5fb4]" />} title={`Checklist items (${clause.checklist_items.length})`}>
                    <ul className="space-y-1 text-[12px]">
                      {clause.checklist_items.map((s, i) => (
                        <li key={i} className="flex gap-2">
                          <span className="font-mono text-[9px] uppercase tracking-wider font-bold text-[#1a5fb4] shrink-0 mt-0.5">
                            {(s.kind || "chk").slice(0, 5)}
                          </span>
                          <span>{s.title}</span>
                        </li>
                      ))}
                    </ul>
                  </Panel>
                )}
              </div>
            )}

            {!clause.description && !clause.tips && !(clause.attachments || []).length && !(clause.vv_sub_tasks || []).length && !(clause.checklist_items || []).length && (
              <div className="py-6 text-center text-[12px] text-[#1a1a1a]/60 bg-[#f7f5f2] rounded-xl">
                No detailed information has been added to this clause yet.
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Panel({ icon, title, children }) {
  return (
    <div className="border border-[#1a1a1a]/08 rounded-xl bg-white overflow-hidden">
      <div className="px-4 py-2 bg-[#f7f5f2] border-b border-[#1a1a1a]/06 flex items-center gap-2">
        {icon}
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#1a1a1a]/75 font-bold">{title}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}

function AttachmentPreview({ att, fileHref }) {
  const mime = att.file_meta?.mime_type || "";
  if (mime.startsWith("image/")) {
    return (
      <div className="aspect-video bg-[#f7f5f2] rounded-lg overflow-hidden">
        <img src={fileHref(att.file_id)} alt={att.title || ""} className="w-full h-full object-cover" />
      </div>
    );
  }
  return (
    <div className="aspect-video bg-gradient-to-br from-[#fff1ea] to-[#f7f5f2] rounded-lg flex items-center justify-center">
      <FileDown className="w-10 h-10 text-[#ff4500]/80" />
    </div>
  );
}
